import { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Image,
  Input,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  AddCircleOutline,
  CloseCircleOutline,
  FileUploadInput,
  TrashBold,
  useToast,
} from '@aslanpay/portal-common-lib';
import { useFieldArray, useFormContext } from 'react-hook-form';

import {
  GALLERY_LIMIT,
  MediaFieldsProps,
  VIDEO_LIMIT,
  MAX_LOGO_SIZE_MB,
  MAX_IMAGE_SIZE_MB,
} from './MediaFields.types';
import AddNewPhotoButton from './AddNewPhotoButton/AddNewPhotoButton';

const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

const readEntry = (entry: FileSystemEntry): Promise<File[]> => {
  return new Promise((resolve) => {
    if (entry.isFile) {
      (entry as FileSystemFileEntry).file((file) => resolve([file]));
    } else if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      reader.readEntries(async (entries) => {
        const nested = await Promise.all(entries.map(readEntry));
        resolve(nested.flat());
      });
    } else {
      resolve([]);
    }
  });
};

const getFilesFromDataTransfer = (
  dataTransfer: DataTransfer
): { entries: FileSystemEntry[]; fallbackFiles: File[] } => {
  const entries: FileSystemEntry[] = [];
  const fallbackFiles: File[] = [];
  const items = Array.from(dataTransfer.items);

  items.forEach((item) => {
    const entry = item.webkitGetAsEntry?.();
    if (entry) {
      entries.push(entry);
    } else if (item.kind === 'file') {
      const file = item.getAsFile();
      if (file) fallbackFiles.push(file);
    }
  });

  return { entries, fallbackFiles };
};

function MediaFields({ uploadFile }: MediaFieldsProps) {
  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { errors, touchedFields },
  } = useFormContext();
  const {
    fields: imageFields,
    remove: removeImage,
    replace: replaceImages,
    move: moveImage,
  } = useFieldArray({ control, name: 'images' });
  const imageDragIndex = useRef<number | null>(null);
  const imageDragOverIndex = useRef<number | null>(null);
  const [imageDragState, setImageDragState] = useState<{ from: number; over: number } | null>(null);
  const {
    fields: videoFields,
    append: appendVideo,
    remove: removeVideo,
    move: moveVideo,
  } = useFieldArray({
    control,
    name: 'videoUrls',
  });
  const videoDragIndex = useRef<number | null>(null);
  const videoDragOverIndex = useRef<number | null>(null);
  const [videoDragState, setVideoDragState] = useState<{ from: number; over: number } | null>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const logoUrl = watch('logoUrl');
  const toast = useToast();

  const handleFormUpdateLogo = (url: string | null) => {
    // removing the logo to an empty string to avoid validation issues
    setValue('logoUrl', url ?? '', { shouldValidate: true, shouldDirty: true });
  };

  const handleError = (message: string) => {
    toast({ position: 'top-right', status: 'error', title: 'Upload error', description: message });
  };

  const validateLogoDimensions = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        if (img.width < 500 || img.height < 500) {
          handleError('Logo must be at least 500x500px.');
          resolve(false);
        } else {
          resolve(true);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        handleError('Failed to read image dimensions.');
        resolve(false);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleLogoUpload = async (file: File) => {
    const isValidSize = await validateLogoDimensions(file);
    if (!isValidSize) return;

    try {
      const url = await uploadFile(file, 'BENEFIT_LOGO');

      handleFormUpdateLogo(url);
    } catch {
      handleError('Failed to upload logo.');
    }
  };

  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleMultipleGalleryUpload = async (files: File[]) => {
    const validImages = files.filter((f) => ACCEPTED_IMAGE_TYPES.includes(f.type));

    if (validImages.length === 0) {
      handleError('No valid images found. Accepted formats: .PNG, .JPG or .JPEG');
      return;
    }

    const totalAfterUpload = imageFields.length + validImages.length;
    if (totalAfterUpload > GALLERY_LIMIT) {
      handleError(
        `Cannot upload ${validImages.length} images. Maximum ${GALLERY_LIMIT} images allowed (${
          GALLERY_LIMIT - imageFields.length
        } remaining).`
      );
      return;
    }

    const oversized = validImages.filter((f) => f.size > MAX_IMAGE_SIZE_MB * 1024 * 1024);
    if (oversized.length > 0) {
      handleError(`${oversized.length} image(s) exceed the ${MAX_IMAGE_SIZE_MB}MB limit.`);
      return;
    }

    setIsUploading(true);
    try {
      const results = await Promise.allSettled(
        validImages.map((file) => uploadFile(file, 'BENEFIT_IMAGE'))
      );

      const uploadedImages = results
        .filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled')
        .map((r) => ({ imageUrl: r.value }));

      const failedCount = results.filter((r) => r.status === 'rejected').length;
      if (failedCount > 0) {
        handleError(`Failed to upload ${failedCount} image(s).`);
      }

      if (uploadedImages.length > 0) {
        const currentImages = (watch('images') || []) as { imageUrl: string }[];
        replaceImages([...currentImages, ...uploadedImages]);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      // Ignore drops from image reordering
      if (imageDragIndex.current !== null) return;

      // Collect entries synchronously before the browser clears dataTransfer
      const { entries, fallbackFiles } = getFilesFromDataTransfer(e.dataTransfer);

      let files: File[];
      if (entries.length > 0) {
        const nested = await Promise.all(entries.map(readEntry));
        files = nested.flat().filter((f) => ACCEPTED_IMAGE_TYPES.includes(f.type));
      } else {
        files = fallbackFiles.filter((f) => ACCEPTED_IMAGE_TYPES.includes(f.type));
      }

      await handleMultipleGalleryUpload(files);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imageFields.length]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  return (
    <Stack spacing="20px" mt="10px">
      {/* Provider Logo */}
      <FormControl isRequired>
        <Flex align="center" gap="6px" mb="4px">
          <FormLabel variant="boldNoMargin">Provider Logo</FormLabel>
          <Text textStyle="smallRegular" color="brand.darkGray">
            (must be a square)
          </Text>
        </Flex>
        <Text textStyle="extraSmallRegular" color="brand.darkGray" mb="10px">
          Max. 2MB. Min. dimensions: 500x500px. Format: .PNG, .JPG or .JPEG
        </Text>
        {logoUrl ? (
          <Box position="relative" w="160px" h="160px">
            <Image
              src={logoUrl}
              w="full"
              h="full"
              objectFit="contain"
              borderRadius="12px"
              border="1px solid"
              borderColor="brand.lightGray1"
            />
            <Flex
              position="absolute"
              inset={0}
              borderRadius="12px"
              bg="brand.tint40"
              align="center"
              justify="center"
              opacity={0}
              _hover={{ opacity: 1 }}
              transition="opacity 0.15s"
            >
              <IconButton
                variant="roundDanger"
                aria-label="Remove logo"
                icon={<TrashBold boxSize="21px" />}
                onClick={() => handleFormUpdateLogo(null)}
                p="8px"
              />
            </Flex>
          </Box>
        ) : (
          <FileUploadInput
            onUpload={handleLogoUpload}
            onError={handleError}
            maxSizeMB={MAX_LOGO_SIZE_MB}
          />
        )}
      </FormControl>

      <Divider borderColor="brand.lightGray1" />

      {/* Photo gallery */}
      <FormControl>
        <Flex justify="space-between" align="center" mb="4px">
          <Box>
            <FormLabel variant="boldNoMargin">Photo gallery</FormLabel>
            <Text textStyle="extraSmallRegular" color="brand.darkGray" mt="2px">
              Max. 5MB. Format: .PNG, .JPG or .JPEG
            </Text>
          </Box>
          <Text textStyle="smallMedium" color="brand.mutedPurple">
            {imageFields.length}/{GALLERY_LIMIT}
          </Text>
        </Flex>
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          border="2px dashed"
          borderColor={isDragOver ? 'brand.purple' : 'brand.lightGray1'}
          bg={isDragOver ? 'brand.lightPurple' : 'transparent'}
          borderRadius="8px"
          p="20px"
          transition="all 0.15s"
        >
          {isUploading && (
            <Flex justify="center" align="center" py="20px">
              <Spinner color="brand.purple" size="lg" />
            </Flex>
          )}
          <input
            ref={galleryInputRef}
            type="file"
            accept=".png,.jpg,.jpeg"
            multiple
            hidden
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length > 0) handleMultipleGalleryUpload(files);
              if (galleryInputRef.current) galleryInputRef.current.value = '';
            }}
          />
          {!isUploading && imageFields.length === 0 && (
            <Text
              textStyle="smallRegular"
              color="brand.darkGray"
              textAlign="center"
              cursor="pointer"
              onClick={() => galleryInputRef.current?.click()}
            >
              Drag and drop images or folders here
            </Text>
          )}
          {!isUploading && imageFields.length > 0 && (
            <Flex gap="12px" flexWrap="wrap">
              {imageFields.map((field, index) => {
                const url = watch(`images.${index}.imageUrl`);
                const isBeingDragged = imageDragState?.from === index;
                const isDragTarget =
                  imageDragState && !isBeingDragged && imageDragState.over === index;

                const getImageScale = () => {
                  if (isBeingDragged) return 'scale(1.05)';
                  if (isDragTarget) return 'scale(0.95)';
                  return 'scale(1)';
                };

                return (
                  <Box
                    key={field.id}
                    position="relative"
                    w="160px"
                    h="160px"
                    draggable
                    cursor="grab"
                    opacity={imageDragState && !isBeingDragged ? 0.4 : 1}
                    transform={getImageScale()}
                    border="2px solid"
                    borderColor={isDragTarget ? 'brand.purple' : 'transparent'}
                    borderRadius="10px"
                    transition="transform 0.2s ease, opacity 0.15s, border-color 0.15s"
                    onDragStart={() => {
                      imageDragIndex.current = index;
                      imageDragOverIndex.current = index;
                      setImageDragState({ from: index, over: index });
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (imageDragIndex.current !== null && imageDragOverIndex.current !== index) {
                        imageDragOverIndex.current = index;
                        setImageDragState({ from: imageDragIndex.current, over: index });
                      }
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDragEnd={() => {
                      if (
                        imageDragIndex.current !== null &&
                        imageDragOverIndex.current !== null &&
                        imageDragIndex.current !== imageDragOverIndex.current
                      ) {
                        moveImage(imageDragIndex.current, imageDragOverIndex.current);
                      }
                      imageDragIndex.current = null;
                      imageDragOverIndex.current = null;
                      setImageDragState(null);
                    }}
                  >
                    <Image src={url} w="full" h="full" objectFit="cover" borderRadius="8px" />
                    <Flex
                      position="absolute"
                      inset={0}
                      borderRadius="8px"
                      bg="brand.tint40"
                      align="center"
                      justify="center"
                      opacity={0}
                      _hover={{ opacity: 1 }}
                      transition="opacity 0.15s"
                    >
                      <IconButton
                        variant="roundDanger"
                        aria-label="Remove photo"
                        icon={<TrashBold boxSize="21px" />}
                        p="8px"
                        onClick={() => removeImage(index)}
                      />
                    </Flex>
                  </Box>
                );
              })}
            </Flex>
          )}
        </Box>

        <AddNewPhotoButton
          ctaText="Upload new photos"
          isDisabled={imageFields.length >= GALLERY_LIMIT}
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            if (files.length > 0) {
              handleMultipleGalleryUpload(files);
            }
          }}
        />
      </FormControl>

      <Divider borderColor="brand.lightGray1" />

      {/* Videos */}
      <FormControl>
        <Flex justify="space-between" align="center" mb="12px">
          <FormLabel variant="boldNoMargin">Videos</FormLabel>
          <Text textStyle="smallMedium" color="brand.mutedPurple">
            {videoFields.length}/{VIDEO_LIMIT}
          </Text>
        </Flex>
        <Stack spacing="12px">
          {videoFields.map((field, index) => {
            const fieldName = `videoUrls.${index}.url` as const;
            const fieldError = (
              errors.videoUrls as Record<string, { url?: { message?: string } }> | undefined
            )?.[index]?.url;
            const isTouched = (
              touchedFields.videoUrls as Record<string, { url?: boolean }> | undefined
            )?.[index]?.url;
            const value = watch(fieldName) || 'https://';

            const isBeingDragged = videoDragState?.from === index;
            let translateY = 0;
            if (videoDragState && !isBeingDragged) {
              const { from, over } = videoDragState;
              if (from < over && index > from && index <= over) {
                translateY = -48;
              } else if (from > over && index >= over && index < from) {
                translateY = 48;
              }
            }

            return (
              <Box
                key={field.id}
                draggable
                cursor="grab"
                opacity={videoDragState && !isBeingDragged ? 0.4 : 1}
                transform={`translateY(${translateY}px)`}
                transition="transform 0.2s ease, opacity 0.15s"
                onDragStart={() => {
                  videoDragIndex.current = index;
                  videoDragOverIndex.current = index;
                  setVideoDragState({ from: index, over: index });
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (videoDragIndex.current !== null && videoDragOverIndex.current !== index) {
                    videoDragOverIndex.current = index;
                    setVideoDragState({ from: videoDragIndex.current, over: index });
                  }
                }}
                onDragEnd={() => {
                  if (
                    videoDragIndex.current !== null &&
                    videoDragOverIndex.current !== null &&
                    videoDragIndex.current !== videoDragOverIndex.current
                  ) {
                    moveVideo(videoDragIndex.current, videoDragOverIndex.current);
                  }
                  videoDragIndex.current = null;
                  videoDragOverIndex.current = null;
                  setVideoDragState(null);
                }}
              >
                <Flex gap="8px" align="center">
                  <Text cursor="grab" userSelect="none" color="brand.darkGray" fontSize="16px">
                    ⠿
                  </Text>
                  <Input
                    value={value}
                    isInvalid={!!fieldError && !!isTouched}
                    onChange={(e) => {
                      let val = e.target.value;
                      if (!val.startsWith('https://')) {
                        val = 'https://';
                      }
                      setValue(fieldName, val);
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pasted = e.clipboardData.getData('text');
                      const stripped = pasted.replace(/^https?:\/\//, '');
                      setValue(fieldName, `https://${stripped}`);
                    }}
                    onBlur={() => {
                      setValue(fieldName, watch(fieldName) || 'https://', { shouldTouch: true });
                      trigger(fieldName);
                    }}
                  />
                  <IconButton
                    variant="link"
                    aria-label="Remove video"
                    icon={<CloseCircleOutline boxSize="24px" color="brand.purple" />}
                    onClick={() => {
                      removeVideo(index);
                      trigger('videoUrls');
                    }}
                  />
                </Flex>
                {fieldError?.message && isTouched && (
                  <Text textStyle="extraSmallRegular" color="system.error" mt="4px">
                    {fieldError.message}
                  </Text>
                )}
              </Box>
            );
          })}
        </Stack>
        <Button
          alignSelf="flex-start"
          variant="link"
          leftIcon={<AddCircleOutline boxSize="20px" />}
          onClick={() => appendVideo({ url: 'https://' })}
          isDisabled={videoFields.length >= VIDEO_LIMIT}
          mt="12px"
          p="0"
        >
          New video URL
        </Button>
      </FormControl>
    </Stack>
  );
}

export default MediaFields;
